import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Divider,
  Tooltip,
  Tabs,
  Tab,
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { StyledAccordion } from "../doctor-console/StyledComponents";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import axios from "axios";
import { toast } from "react-toastify";
import { displayPatientName } from "../../utils/displayPatientName";
import { render } from "react-dom";


// ─── Constants ────────────────────────────────────────────────────────────────

const yesNoFields = [
  "smoking",
  "drinking",
  "tobacco",
  "hypoglycemia",
  "diabetic_foot",
  "peripheral_neuropathy",
  "peripheral_vascular_disease",
  "nephropathy",
  "ckd",
  "diabetic_retinopathy",
  "hypertension",
  "metabolic_syndrome",
];

const leftRightCheckboxFields = [
  "previous_ulcer",
  "previous_amputation",
  "absent_pedal_pulses",
  "active_ulcer",
  "callus",
  "blisters",
  "fissures",
  "neuropathy",
  "corn",
  "nails_thickened",
  "nails_ingrown",
  "nails_fungal",
  "deformities_claw_toes",
  "deformities_hammer_toes",
  "deformities_hallux_valgus",
  "deformities_flat_foot",
  "deformities_charcot_foot",
  "infection_redness",
  "infection_pus",
  "infection_swelling",
];

const SelectOptions = {
  exercise: ["Sedentary", "Mild", "Moderate", "Severe"],
  diet: ["Vegetarian", "Vegan", "High Protein", "Pure Vegetarian", "Non Vegetarian"],
  diabetes_type: ["Type 1", "Type 2", "Gestational", "Other"],
  left_foot_risk: ["Low", "Moderate", "High"],
  right_foot_risk: ["Low", "Moderate", "High"],
  skin_hydration: ["Dry & Cracking", "Bacterial", "Fungal", "Diabetic Dermopathy"],
  teeth_status: ["Caries", "Discoloration", "Mobility", "Broken"],
  gums_status: ["Redness", "Swelling", "Bleeding", "Receding", "Tender"],
  cardiovascular_risk: ["Low", "Moderate", "High"],
};

const initialFormData = {
  medical_history: "",
  allergies: "",
  first_complaints: "",
  current_treatment: "",
  previous_treatment: "",
  family_history: "",
  review: "",
  smoking: "",
  drinking: "",
  tobacco: "",
  exercise: "",
  description: "",
  diet: "",
  diabetes_type: "",
  diabetes_onset_time: "",
  diabetes_onset_from: "",
  hypoglycemia: "",
  diabetic_foot: "",
  peripheral_neuropathy: "",
  peripheral_vascular_disease: "",
  nephropathy: "",
  ckd: "",
  diabetic_retinopathy: "",
  hypertension: "",
  hypertension_onset_time: "",
  lipid_disorders: "",
  other_ailments: "",
  skin_normal: false,
  skin_hydration: "",
  skin_stretchability: "",
  skin_texture: "",
  foot_examination: "",
  advice: "",
  left_foot_risk: "",
  right_foot_risk: "",
  previous_ulcer: { left: false, right: false },
  previous_amputation: { left: false, right: false },
  absent_pedal_pulses: { left: false, right: false },
  active_ulcer: { left: false, right: false },
  callus: { left: false, right: false },
  blisters: { left: false, right: false },
  fissures: { left: false, right: false },
  neuropathy: { left: false, right: false },
  corn: { left: false, right: false },
  nails_thickened: { left: false, right: false },
  nails_ingrown: { left: false, right: false },
  nails_fungal: { left: false, right: false },
  deformities_claw_toes: { left: false, right: false },
  deformities_hammer_toes: { left: false, right: false },
  deformities_hallux_valgus: { left: false, right: false },
  deformities_flat_foot: { left: false, right: false },
  deformities_charcot_foot: { left: false, right: false },
  infection_redness: { left: false, right: false },
  infection_pus: { left: false, right: false },
  infection_swelling: { left: false, right: false },
  quantitative_avg_vpt: { left: "", right: "" },
  foot_normal: false,
  teeth_status: "",
  gums_status: "",
  duration: "",
  symptoms: "",
  progression: "",
  drug_allergies: "",
  food_allergies: "",
  surgeries_history: "",
  vaccination_status: "",
  fasting_blood_sugar: "",
  post_prandial_blood_sugar: "",
  hba1c: "",
  lipid_profile: "",
  liver_function_tests: "",
  kidney_function_tests: "",
  thyroid_profile: "",
  cbc: "",
  fib_4: "",
  ucar: "",
  other_investigations: "",
  primary_diagnosis: "",
  secondary_diagnosis: "",
  patient_motivation: "",
  current_medications: "",
  medication_compliance: "",
  medication_side_effects: "",
  lifestyle_modifications: "",
  diet_plan: "",
  exercise_plan: "",
  treatment_medications: "",
  next_visit: "",
  monitoring_plan: "",
  cardiovascular_risk: "",
  metabolic_syndrome: "",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalizeIncomingData = (payload) => {
  if (!payload || typeof payload !== "object") return {};
  const raw = payload.data || payload.case_sheet || payload.caseSheet || payload;
  return Array.isArray(raw) ? (raw[0] ?? {}) : raw;
};

const toBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const v = value.toLowerCase();
    return v === "true" || v === "yes" || v === "1";
  }
  return Boolean(value);
};

const buildMergedFormData = (incomingData) => {
  const merged = { ...initialFormData };
  Object.keys(initialFormData).forEach((key) => {
    const fallbackValue = initialFormData[key];
    const incoming = incomingData?.[key];
    if (incoming === undefined || incoming === null) return;
    if (key === "quantitative_avg_vpt") {
      merged[key] = {
        left: String(incoming?.left ?? fallbackValue.left ?? ""),
        right: String(incoming?.right ?? fallbackValue.right ?? ""),
      };
      return;
    }
    if (leftRightCheckboxFields.includes(key)) {
      merged[key] = {
        left: toBool(incoming?.left ?? false),
        right: toBool(incoming?.right ?? false),
      };
      return;
    }
    merged[key] = incoming;
  });
  return merged;
};

const toTitleCase = (str) =>
  str
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");

const normalizeLeftRightPayload = (data) => {
  const cleaned = { ...data };
  leftRightCheckboxFields.forEach((field) => {
    const val = cleaned[field];
    if (val && typeof val === "object") {
      const newVal = {};
      if (val.left) newVal.left = true;
      if (val.right) newVal.right = true;
      cleaned[field] = newVal;
    }
  });
  return cleaned;
};

// ─── Design tokens ────────────────────────────────────────────────────────────

// FIX 1: Header matches PaymentModal exactly — #213360
const PAYMENT_HEADER_BG = "#213360";

// FIX 2: Section bar color — matches "Mark as Paid" button style (#1a3a6b deep navy)
// This is the deep navy used on action buttons across the app
const SECTION_BAR_BG = "#1a3a6b";

const ACCENT = "#19C2B0";
const ACCENT_DARK = "#13A596";
const SURFACE = "#F4F7FA";
const LABEL_COLOR = "#4A5568";
const BORDER = "#DDE4EC";
const BRAND = "#0E3F66";
const BRAND_LIGHT = "#1A5A8A";

// ─── Local Styled Accordions (White Background) ────────────────────────────

const CaseSheetCustomAccordion = styled(MuiAccordion)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  boxShadow: "none",
  borderRadius: "6px",
  marginBottom: theme.spacing(1),
  border: "1px solid #B1B1B1BB",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: theme.spacing(0.5, 0),
    backgroundColor: "#FFFFFF",
    border: "1px solid #4DD7CD",
  },
}));

const CaseSheetCustomAccordionSummary = styled(MuiAccordionSummary, {
  shouldForwardProp: (prop) => prop !== "hasHeaderContent",
})(({ hasHeaderContent }) => ({
  minHeight: "42px",
  padding: "0 12px",
  "& .MuiAccordionSummary-content": {
    margin: 0,
    width: "100%",
    display: "flex",
    flexDirection: hasHeaderContent ? "column" : "row",
    alignItems: "center",
  },
  "&.Mui-expanded": {
    backgroundColor: "#FFFFFF",
  },
  "& .MuiTypography-root": {
    fontSize: "14px",
    fontWeight: 500,
    color: "#2a3c50",
    textAlign: "left",
  },
}));

const CaseSheetCustomAccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: "12px 16px",
  backgroundColor: "#FFFFFF",
  borderTop: "1px solid #e0e0e0",
  ".Mui-expanded &": {
    borderTop: "none",
  },
}));

function CaseSheetStyledAccordion({
  title,
  actions,
  headerContent,
  children,
  expanded,
  onChange,
  ...props
}) {
  return (
    <CaseSheetCustomAccordion expanded={expanded} onChange={onChange} {...props}>
      <CaseSheetCustomAccordionSummary
        expandIcon={<ExpandMoreIcon />}
        hasHeaderContent={!!headerContent}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography sx={{ flex: 1 }}>{title}</Typography>
          {actions && <Box sx={{ mr: 2 }}>{actions}</Box>}
        </Box>

        {headerContent ? <Box sx={{ width: "97%" }}>{headerContent}</Box> : null}
      </CaseSheetCustomAccordionSummary>

      <CaseSheetCustomAccordionDetails>{children}</CaseSheetCustomAccordionDetails>
    </CaseSheetCustomAccordion>
  );
}

// ─── Shared field styles ──────────────────────────────────────────────────────

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fff",
    fontSize: 13,
    transition: "box-shadow 0.2s",
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: BRAND_LIGHT },
    "&.Mui-focused fieldset": { borderColor: ACCENT, borderWidth: "1.5px" },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${ACCENT}22` },
  },
  "& .MuiInputBase-input": { 
    fontSize: 13, 
    py: "10px", 
    lineHeight: 1.5 // Added for readability in multiline
  },
  "& .MuiInputBase-inputMultiline": {
    paddingTop: 0, // MUI handles padding inside multiline differently
    paddingBottom: 0,
  },
  "& .MuiSelect-select": { fontSize: 13, py: "8px" },
};

// FIX 3: Label weight bumped to 600 — more readable and professional
const labelSx = {
  color: LABEL_COLOR,
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.4,
};

const rowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "minmax(150px, 185px) 1fr" },
  gap: { xs: 1, sm: 2 },
  alignItems: { xs: "stretch", sm: "center" },
};

// ─── Case sheet section: uses CaseSheetStyledAccordion with white background ──

function CaseSheetSection({ title, defaultExpanded = true, children }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const handleChange = useCallback((_, next) => {
    setExpanded(next);
  }, []);
  return (
    <CaseSheetStyledAccordion title={title} expanded={expanded} onChange={handleChange}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: 1.75, sm: 2.25 },
        }}
      >
        {children}
      </Box>
    </CaseSheetStyledAccordion>
  );
}

// ─── Error state ──────────────────────────────────────────────────────────────

function FetchErrorState({ message, onRetry }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        py: 10,
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 48, color: "#E05A5A" }} />
      <Typography
        sx={{ fontSize: 14, fontWeight: 600, color: "#444", textAlign: "center", px: 4 }}
      >
        {message}
      </Typography>
      {onRetry && (
        <Button
          variant="outlined"
          size="small"
          onClick={onRetry}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            borderColor: BRAND,
            color: BRAND,
          }}
        >
          Retry
        </Button>
      )}
    </Box>
  );
}

// ─── Tab constants ────────────────────────────────────────────────────────────

const SHEET_TAB_DIABETES = "diabetes";
const SHEET_TAB_FOOT = "foot";

// ─── Main Component ───────────────────────────────────────────────────────────

function CaseSheetModal({ open, onClose, appointment, patientData, onAfterSave }) {
  const [formData, setFormData] = useState(initialFormData);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeSheetTab, setActiveSheetTab] = useState(SHEET_TAB_DIABETES);

  const backendUrl = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const authToken = sessionStorage.getItem("accessToken") ?? "";
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${authToken}` }),
    [authToken]
  );

  const patientId = appointment?.patient;


  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchCaseSheet = useCallback(async () => {
    if (!patientId) {
      setFormData(initialFormData);
      setFetchError("Patient ID not found for selected appointment.");
      return;
    }
    setIsFetching(true);
    setFetchError(null);
    try {
      const response = await axios.get(
        `${backendUrl}create_patient_diabetic_profile/?patient_id=${encodeURIComponent(patientId)}`,
        { headers }
      );
      const normalized = normalizeIncomingData(response?.data);
      setFormData(buildMergedFormData(normalized));
    } catch (error) {
      setFormData(initialFormData);
      if (error?.response?.status === 404) {
        setFetchError(null);
        toast.info("No existing case sheet found. Fill in and save to create one.");
      } else if (
        error?.response?.status === 401 ||
        error?.response?.status === 403
      ) {
        setFetchError("Session expired. Please log in again.");
      } else if (!navigator.onLine) {
        setFetchError("No internet connection. Please check your network.");
      } else {
        setFetchError("Unable to load case sheet data. Please try again.");
      }
    } finally {
      setIsFetching(false);
    }
  }, [patientId, backendUrl, headers]);

  useEffect(() => {
    if (!open) return;
    fetchCaseSheet();
  }, [open, fetchCaseSheet]);

  // Reset tab to Diabetes whenever modal opens
  useEffect(() => {
    if (open) setActiveSheetTab(SHEET_TAB_DIABETES);
  }, [open]);

  const handleSheetTabChange = (_, next) => {
    if (next) setActiveSheetTab(next);
  };

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleTextChange = (key) => (event) => {
    setFormData((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleYesNoChange = (key) => (_, nextValue) => {
    if (!nextValue) return;
    setFormData((prev) => ({ ...prev, [key]: nextValue }));
  };

  const handleLeftRightCheck = (key, side) => (event) => {
    const checked = event.target.checked;
    setFormData((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [side]: checked },
    }));
  };

  const handleLeftRightText = (side) => (event) => {
    setFormData((prev) => ({
      ...prev,
      quantitative_avg_vpt: {
        ...(prev.quantitative_avg_vpt || {}),
        [side]: event.target.value,
      },
    }));
  };

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (isSaving) return;
    if (!patientId) {
      toast.error("Patient ID missing. Cannot save case sheet.");
      return;
    }
    if (!backendUrl) {
      toast.error("Backend URL is not configured. Contact support.");
      return;
    }
    if (!navigator.onLine) {
      toast.error("No internet connection. Please check your network.");
      return;
    }
    const vpt = formData?.quantitative_avg_vpt || {};
    const vptSideValid = (raw) => {
      if (raw === undefined || raw === null) return true;
      const s = String(raw).trim();
      if (s === "") return true;
      const n = Number(s);
      return Number.isFinite(n);
    };
    if (!vptSideValid(vpt.left) || !vptSideValid(vpt.right)) {
      toast.error(
        "Quantitative Avg. VPT must be a valid number or left blank."
      );
      return;
    }
    setIsSaving(true);
    try {
      const transformedData = normalizeLeftRightPayload(formData);
      const payload = {
        ...transformedData,
        patient_id: patientId,
      };
      console.log("Case sheet save payload:", payload);
      const response = await axios.post(
        `${backendUrl}create_patient_diabetic_profile/`,
        payload,
        { headers }
      );
      if (
        response?.data?.status === "Success" ||
        response?.status === 200
      ) {
        toast.success("Case sheet saved successfully.");
        if (typeof onAfterSave === "function") onAfterSave(formData);
        onClose?.();
      } else {
        toast.error(
          response?.data?.message || "Unexpected response. Please try again."
        );
      }
    } catch (error) {
      if (
        error?.response?.status === 401 ||
        error?.response?.status === 403
      ) {
        toast.error("Session expired. Please log in again.");
      } else if (error?.response?.status === 422) {
        toast.error(
          "Validation error. Please check all fields and try again."
        );
      } else if (!navigator.onLine) {
        toast.error("No internet connection.");
      } else {
        toast.error(
          error?.response?.data?.message ||
          "Unable to save case sheet. Please try again."
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  // ── Field renderers ────────────────────────────────────────────────────────

  // FIX 6: Placeholder text simplified — no "Enter …" prefix, just the field name
  const renderTextField = (label, key, placeholder, multiline = true) => (
    <Box key={key} sx={rowSx}>
      <Typography sx={labelSx}>{label}</Typography>
      <TextField
        size="small"
        value={formData[key] ?? ""}
        onChange={handleTextChange(key)}
        placeholder={placeholder}
        fullWidth
        disabled={isFetching}
        autoComplete="off"
        sx={inputSx}
        multiline={multiline}
        minRows={multiline ? 1 : 1}
      />
    </Box>
  );

  const renderSelectField = (label, key) => (
    <Box key={key} sx={rowSx}>
      <Typography sx={labelSx}>{label}</Typography>
      <TextField
        select
        size="small"
        value={formData[key] ?? ""}
        onChange={handleTextChange(key)}
        fullWidth
        disabled={isFetching}
        sx={inputSx}
      >
        <MenuItem value="" disabled>
          <em
            style={{ color: "#aaa", fontStyle: "normal", fontSize: 13 }}
          >
            Select…
          </em>
        </MenuItem>
        {(SelectOptions[key] || []).map((option) => (
          <MenuItem key={option} value={option} sx={{ fontSize: 13 }}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  const caseSheetToggleButtonGroupSx = {
    gap: 0.5,
    "& .MuiToggleButton-root": {
      border: `1px solid ${BORDER} !important`,
      borderRadius: "8px !important",
      textTransform: "none",
      px: 2.5,
      py: 0.55,
      fontSize: 13,
      fontWeight: 500,
      color: LABEL_COLOR,
      transition: "all 0.15s",
      "&.Mui-selected": {
        backgroundColor: ACCENT,
        color: "#fff",
        borderColor: `${ACCENT} !important`,
        fontWeight: 600,
        "&:hover": { backgroundColor: ACCENT_DARK },
      },
      "&:hover:not(.Mui-selected)": {
        backgroundColor: `${ACCENT}15`,
        borderColor: `${ACCENT} !important`,
        color: ACCENT_DARK,
      },
    },
  };

  const renderYesNoField = (label, key) => (
    <Box key={key} sx={rowSx}>
      <Typography sx={labelSx}>{label}</Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={formData[key] ?? ""}
        onChange={handleYesNoChange(key)}
        disabled={isFetching}
        sx={caseSheetToggleButtonGroupSx}
      >
        <ToggleButton value="Yes">Yes</ToggleButton>
        <ToggleButton value="No">No</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );

  const renderSkinHydrationField = () => (
    <Box key="skin_hydration" sx={rowSx}>
      <Typography sx={labelSx}>Hydration</Typography>
      <TextField
        select
        size="small"
        value={formData.skin_hydration ?? ""}
        onChange={handleTextChange("skin_hydration")}
        fullWidth
        disabled={isFetching}
        sx={inputSx}
      >
        <MenuItem value="" disabled>
          <em
            style={{ color: "#aaa", fontStyle: "normal", fontSize: 13 }}
          >
            Select Hydration
          </em>
        </MenuItem>
        {(SelectOptions.skin_hydration || []).map((option) => (
          <MenuItem key={option} value={option} sx={{ fontSize: 13 }}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  const renderOptionToggleField = (label, key, options) => (
    <Box key={key} sx={rowSx}>
      <Typography sx={labelSx}>{label}</Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={formData[key] ?? ""}
        onChange={handleYesNoChange(key)}
        disabled={isFetching}
        sx={caseSheetToggleButtonGroupSx}
      >
        {options.map((opt) => (
          <ToggleButton key={opt} value={opt}>
            {opt}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );

  const renderLeftRightCheckbox = (label, key) => (
    <Box key={key} sx={rowSx}>
      <Typography sx={labelSx}>{label}</Typography>
      <Box sx={{ display: "flex", gap: 3 }}>
        {["left", "right"].map((side) => (
          <FormControlLabel
            key={side}
            control={
              <Checkbox
                checked={Boolean(formData?.[key]?.[side])}
                onChange={handleLeftRightCheck(key, side)}
                disabled={isFetching}
                size="small"
                sx={{
                  color: BORDER,
                  "&.Mui-checked": { color: ACCENT },
                  p: 0.5,
                }}
              />
            }
            label={
              <Typography
                sx={{ fontSize: 13, fontWeight: 500, color: LABEL_COLOR }}
              >
                {side.charAt(0).toUpperCase() + side.slice(1)}
              </Typography>
            }
          />
        ))}
      </Box>
    </Box>
  );

  // ── Patient display info ───────────────────────────────────────────────────
  // FIX 7: Resolve patient name from all possible keys the appointment object
  // might carry. Falls back gracefully to Patient ID so header is never empty.
  const patientDisplayName = (() => {
    const name =
      appointment?.patient_name ||
      appointment?.name ||
      appointment?.patientName ||
      appointment?.patient?.name ||
      appointment?.patient?.patient_name ||
      "";
    return String(name).trim();
  })();

  const patientSubtitle = patientId
    ? `Patient ID: ${patientId}`
    : "—";

  const saveDisabled = isSaving || isFetching || Boolean(fetchError);

  const saveButtonSx = (placement) => ({
    textTransform: "none",
    borderRadius: "8px",
    backgroundColor: "#213360",
    fontSize: 13,
    fontWeight: 600,
    px: 2.5,
    py: 0.75,
    minHeight: 36,
    minWidth: { xs: 104, sm: 112 },
    boxShadow: "none",
    "&:hover": { backgroundColor: ACCENT_DARK, boxShadow: "none" },
    ...(placement === "header"
      ? {
        "&.Mui-disabled": {
          backgroundColor: "rgba(255,255,255,0.2)",
          color: "rgba(255,255,255,0.45)",
        },
      }
      : {
        "&.Mui-disabled": {
          backgroundColor: "#e0e0e0",
          color: "#9e9e9e",
        },
      }),
  });

  const renderSaveButton = (placement) => (
    <Tooltip title={isSaving ? "Saving…" : "Save case sheet"} arrow>
      <span>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saveDisabled}
          sx={saveButtonSx(placement)}
        >
          {isSaving ? "Saving…" : "Save"}
        </Button>
      </span>
    </Tooltip>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      // onClose={isSaving ? undefined : onClose}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return; // backdrop click ignore
        if (!isSaving) onClose?.();
      }}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 64px rgba(33,51,96,0.22)",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: PAYMENT_HEADER_BG,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          py: 1.5,
          px: 2,
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0, flex: "1 1 200px" }}>
          <Typography
            component="div"
            sx={{
              color: "#FFFFFF",
              fontWeight: 600,
              fontSize: { xs: 16, sm: 18 },
              lineHeight: 1.25,
            }}
          >
            Case Sheet / Diabetes Profile
          </Typography>
          <Typography
            component="div"
            sx={{
              color: "rgba(255,255,255,0.9)",
              fontWeight: 500,
              fontSize: { xs: 13, sm: 14 },
              mt: 0.35,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={patientSubtitle}
          >
            {patientSubtitle}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={isSaving}
          sx={{
            color: "#FFFFFF",
            flexShrink: 0,
            "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: { xs: "1.2rem", sm: "1.4rem" } }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "hidden",
          backgroundColor: SURFACE,
        }}
      >
        {/* ── Loading ── */}
        {isFetching && (
          <Box
            sx={{
              flex: "1 1 auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 12,
              gap: 2,
              minHeight: 280,
            }}
          >
            <CircularProgress size={36} sx={{ color: ACCENT }} />
            <Typography sx={{ color: LABEL_COLOR, fontSize: 13 }}>
              Loading case sheet…
            </Typography>
          </Box>
        )}

        {/* ── Fetch Error ── */}
        {!isFetching && fetchError && (
          <Box sx={{ flex: "1 1 auto", overflowY: "auto", minHeight: 0 }}>
            <FetchErrorState message={fetchError} onRetry={fetchCaseSheet} />
          </Box>
        )}

        {/* ── Main Content ── */}
        {!isFetching && !fetchError && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: "1 1 auto",
              minHeight: 0,
              // FIX 10: No mx/mt gap — tabs flush against header, no grey rectangle
              mx: 0,
              mt: 0,
            }}
          >
            {/* ── Tabs — flush, no gap below header ── */}
            <Tabs
              value={activeSheetTab}
              onChange={handleSheetTabChange}
              variant="fullWidth"
              textColor="inherit"
              TabIndicatorProps={{ style: { display: "none" } }}
              sx={{
                flexShrink: 0,
                minHeight: 44,
                backgroundColor: "#FFFFFF",
                // FIX 11: borderBottom only — no top border so tabs kiss the header
                borderBottom: `1px solid ${BORDER}`,
                // FIX 12: Correct way to gap tabs inside MUI Tabs
                "& .MuiTabs-flexContainer": { gap: "0px", height: "100%" },
              }}
            >
              {[
                { value: SHEET_TAB_DIABETES, label: "Diabetes" },
                { value: SHEET_TAB_FOOT, label: "Diabetic Foot" },
              ].map(({ value, label }) => (
                <Tab
                  key={value}
                  label={label}
                  value={value}
                  disableRipple
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    textTransform: "none",
                    minHeight: 44,
                    flex: 1,
                    // FIX 13: Clean active/inactive tab — no individual borders that create gap
                    borderBottom:
                      activeSheetTab === value
                        ? `3px solid ${ACCENT}`
                        : "3px solid transparent",
                    bgcolor:
                      activeSheetTab === value
                        ? `${ACCENT}10`   // very light teal tint on active
                        : "#FFFFFF",
                    color:
                      activeSheetTab === value ? ACCENT_DARK : "#555",
                    transition: "all 0.2s ease-in-out",
                    borderRadius: 0,
                    "&:hover": {
                      bgcolor: `${ACCENT}08`,
                      color: ACCENT_DARK,
                    },
                  }}
                />
              ))}
            </Tabs>

            {/* ── Patient Info Strip ── */}
            <Box
              sx={{
                flexShrink: 0,
                px: 2.5,
                py: 1.5,
                backgroundColor: "#F9FAFB",
                borderBottom: `1px solid ${BORDER}`,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#4A5568",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {(() => {
                  const name = displayPatientName(
                    patientData?.name ||
                      patientData?.patient_name ||
                      patientDisplayName,
                    "—"
                  );
                  const gender = patientData?.gender || "—";
                  const age = patientData?.age ?? null;
                  const ageStr = age !== null && age !== undefined ? `${age} years` : "—";
                  return `Patient Name: ${name}, Sex: ${gender}, Age: ${ageStr}`;
                })()}
              </Typography>
            </Box>

            {/* ── Tab panel — this is where scroll lives ── */}
            <Box
              sx={{
                flex: "1 1 auto",
                minHeight: 0,
                maxHeight: { xs: "calc(92vh - 200px)", sm: "calc(92vh - 180px)" },
                overflowY: "auto",
                p: 2.5,
                pb: 3,
                WebkitOverflowScrolling: "touch",
                backgroundColor: SURFACE,
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  background: `${BRAND}40`,
                  borderRadius: 4,
                  "&:hover": { background: `${BRAND}75` },
                },
              }}
            >
              {/* ── Diabetes Tab ── */}
              {activeSheetTab === SHEET_TAB_DIABETES && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <CaseSheetSection title="GENERAL" defaultExpanded>
                    {renderTextField("Medical History", "medical_history", "Medical history")}
                    {renderTextField("Allergies", "allergies", "Allergies")}
                    {renderTextField("First Complaints", "first_complaints", "First complaints")}
                    {renderTextField("Current Treatment", "current_treatment", "Current treatment")}
                    {renderTextField("Previous Treatment", "previous_treatment", "Previous treatment")}
                    {renderTextField("Family History", "family_history", "Family history")}
                    {renderTextField("Review", "review", "Review")}
                  </CaseSheetSection>

                  <CaseSheetSection title="LIFESTYLE" defaultExpanded>
                    {renderYesNoField("Smoking", "smoking")}
                    {renderYesNoField(" Alcohol Drinking", "drinking")}
                    {renderYesNoField("Tobacco", "tobacco")}
                    {renderSelectField("Exercise", "exercise")}
                    {renderTextField("Description", "description", "Additional lifestyle details")}
                    {renderSelectField("Diet", "diet")}
                  </CaseSheetSection>

                  <CaseSheetSection title="DIABETES PROFILE" defaultExpanded>
                    {renderSelectField("Diabetes Type", "diabetes_type")}
                    {renderTextField("Onset Time", "diabetes_onset_time", "e.g. 5 years ago")}
                    {renderTextField("Diabetes Onset From", "diabetes_onset_from", "Onset details")}
                    <Divider sx={{ borderColor: BORDER }} />
                    {yesNoFields.slice(3,11).map((fk) =>
                      renderYesNoField(toTitleCase(fk), fk)
                    )}
                    <Divider sx={{ borderColor: BORDER }} />
                    {renderTextField("Hypertension Onset", "hypertension_onset_time", "e.g. 2 years ago")}
                    {renderTextField("Lipid Disorders", "lipid_disorders", "Lipid disorders")}
                    {renderTextField("Other Ailments", "other_ailments", "Other ailments")}
                  </CaseSheetSection>

                  <CaseSheetSection title="SKIN EXAMINATION" defaultExpanded>
                    <Box sx={rowSx}>
                      <Typography sx={labelSx}>Status</Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.skin_normal}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setFormData((prev) => ({
                                ...prev,
                                skin_normal: checked,
                                ...(checked && {
                                  skin_hydration: "",
                                  skin_stretchability: "",
                                  skin_texture: "",
                                }),
                              }));
                            }}
                            disabled={isFetching}
                            size="small"
                            sx={{
                              color: BORDER,
                              "&.Mui-checked": { color: ACCENT },
                              p: 0.5,
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: LABEL_COLOR,
                              userSelect: "none",
                            }}
                          >
                            Normal (No examination required)
                          </Typography>
                        }
                      />
                    </Box>

                  
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: { xs: 1.75, sm: 2.25 },
                        pointerEvents: formData.skin_normal ? "none" : "auto",
                        opacity: formData.skin_normal ? 0.45 : 1,
                        transition: "opacity 0.2s ease",
                      }}
                    >
                      {renderSkinHydrationField()}
                      {renderOptionToggleField(
                        "Stretchability",
                        "skin_stretchability",
                        ["Good", "Poor"]
                      )}
                      {renderOptionToggleField("Texture", "skin_texture", [
                        "Thickened",
                        "Rough",
                      ])}
                    </Box>
                  </CaseSheetSection>
                  <CaseSheetSection title="DENTAL STATUS" defaultExpanded>
                    {renderSelectField("Teeth", "teeth_status")}
                    {renderSelectField("Gums", "gums_status")}
                  </CaseSheetSection>
                  <CaseSheetSection title="HISTORY OF PRESENT ILLNESS" defaultExpanded>
                    {renderTextField("Duration", "duration", "Duration of current illness")}
                    {renderTextField("Symptoms", "symptoms", "Symptoms details")}
                    {renderTextField("Progression", "progression", "Progression details")}
                  </CaseSheetSection>
                  <CaseSheetSection title="SURGICAL HISTORY" defaultExpanded>
                    {renderTextField("Surgeries History", "surgeries_history", "Details of past surgeries")}
                    </CaseSheetSection>
                    <CaseSheetSection title="VACCINATION STATUS" defaultExpanded>
                    {renderTextField("Vaccination Status", "vaccination_status", "Details of vaccinations received")}
                    </CaseSheetSection> 
                    <CaseSheetSection title="LABORATORY INVESTIGATIONS" defaultExpanded>
                      {renderTextField("Fasting Blood Sugar", "fasting_blood_sugar", "Fasting blood sugar details")}
                      {renderTextField("Post Prandial Blood Sugar", "post_prandial_blood_sugar", "Post prandial blood sugar details ")}
                      {renderTextField("HbA1c", "hba1c", "HbA1c details")}
                      {renderTextField("Lipid Profile", "lipid_profile", "Lipid profile details")}
                      {renderTextField("Liver Function Tests", "liver_function_tests", "Liver function tests details")}
                      {renderTextField("Kidney Function Tests", "kidney_function_tests", "Kidney function tests details")}
                      {renderTextField("Thyroid Profile", "thyroid_profile", "Thyroid profile details")}
                      {renderTextField("CBC", "cbc", "CBC details")}
                      {renderTextField("FIB-4", "fib_4", "FIB-4 index details")}
                      {renderTextField("UCAR", "ucar", "UACR details")}
                      {renderTextField("Others", "other_investigations", "Other investigations details")}
                    </CaseSheetSection>
                    <CaseSheetSection title ="RISK STRATIFICATION" defaultExpanded>
                      {renderSelectField("Cardiovascular Risk", "cardiovascular_risk")}
                      {renderYesNoField("Metabolic Syndrome", "metabolic_syndrome")}
                      </CaseSheetSection>
                    <CaseSheetSection title="DIAGNOSIS" defaultExpanded>
                    {renderTextField("Primary(Provisional)Diagnosis", "primary_diagnosis", "Diagnosis details")}
                    {renderTextField("Secondary(Definitive) Diagnosis", "secondary_diagnosis", "Secondary diagnosis details")}
                      </CaseSheetSection>
                 
                      <CaseSheetSection title="MEDICATION HISTORY" defaultExpanded>
                        {renderTextField("Current Medications", "current_medications", "Details of current medications  ")}
                        {renderOptionToggleField("Compliance", "medication_compliance", [
                          "Good",
                          "Average",
                          "Poor",
                        ])}
                        {renderTextField("Side Effects", "medication_side_effects", "Details of any medication side effects experienced")}
                        <Divider sx={{ borderColor: BORDER }} />
                        {renderTextField("Drug Allergies", "drug_allergies", "List drug allergies")}
                        {renderTextField("Food Allergies", "food_allergies", "List food allergies")}
                      </CaseSheetSection>
                      <CaseSheetSection title="TREATMENT PLAN" defaultExpanded>
                        {renderTextField("Lifestyle Modifications", "lifestyle_modifications", "Recommended lifestyle modifications")}
                        {renderTextField("Diet Plan", "diet_plan")}
                        {renderTextField("Exercise", "exercise_plan")}
                        {renderTextField("Medications", "treatment_medications")}
                      </CaseSheetSection>
                       <CaseSheetSection title="FOLLOW-UP PLAN" defaultExpanded>  
                        {renderTextField("Next Visit", "next_visit", "Details of the next follow-up visit")}
                        {renderTextField("Monitoring", "monitoring_plan", "Monitoring plan details")}
                       </CaseSheetSection>
                          <CaseSheetSection title="PATIENT MOTIVATION" defaultExpanded>
                      {renderOptionToggleField("Patient Motivation", "patient_motivation", [
                        "High",
                        "Moderate",
                        "Poor",// here doubt
                      ]
                      )}
                      </CaseSheetSection>
                </Box>
              )}

              {/* ── Diabetic Foot Tab ── */}
              {activeSheetTab === SHEET_TAB_FOOT && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <CaseSheetSection title="PODIATRY" defaultExpanded>
                    {renderTextField("Foot Examination", "foot_examination", "Foot examination findings")}
                    {renderTextField("Advice", "advice", "Advice")}
                  </CaseSheetSection>

                  <CaseSheetSection title="FOOT RISK" defaultExpanded>
                    {renderSelectField("Left Foot Risk", "left_foot_risk")}
                    {renderSelectField("Right Foot Risk", "right_foot_risk")}
                  </CaseSheetSection>

                  <CaseSheetSection title="FOOT ASSESSMENT" defaultExpanded>
                    <Box sx={rowSx}>
                      <Typography sx={labelSx}>Status</Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.foot_normal}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setFormData((prev) => {
                                const updates = {
                                  ...prev,
                                  foot_normal: checked,
                                };
                                if (checked) {
                                  // Clear all foot assessment fields
                                  leftRightCheckboxFields.forEach((field) => {
                                    updates[field] = {
                                      left: false,
                                      right: false,
                                    };
                                  });
                                  updates.quantitative_avg_vpt = {
                                    left: "",
                                    right: "",
                                  };
                                }
                                return updates;
                              });
                            }}
                            disabled={isFetching}
                            size="small"
                            sx={{
                              color: BORDER,
                              "&.Mui-checked": { color: ACCENT },
                              p: 0.5,
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: LABEL_COLOR,
                              userSelect: "none",
                            }}
                          >
                            Normal (No examination required)
                          </Typography>
                        }
                      />
                    </Box>

                   
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: { xs: 1.75, sm: 2.25 },
                        pointerEvents: formData.foot_normal ? "none" : "auto",
                        opacity: formData.foot_normal ? 0.45 : 1,
                        transition: "opacity 0.2s ease",
                      }}
                    >
                      {/* Column headers */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "minmax(150px, 185px) 1fr",
                          },
                          gap: { xs: 1, sm: 2 },
                          mb: 1,
                        }}
                      >
                        <Box />
                        <Box
                          sx={{
                            display: "flex",
                            gap: 3,
                            pl: { xs: 0, sm: 0.5 },
                          }}
                        >
                          {["Left", "Right"].map((side) => (
                            <Typography
                              key={side}
                              sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: SECTION_BAR_BG,
                                letterSpacing: "0.6px",
                                minWidth: 72,
                              }}
                            >
                              {side.toUpperCase()}
                            </Typography>
                          ))}
                        </Box>
                      </Box>

                      {leftRightCheckboxFields.map((fk) =>
                        renderLeftRightCheckbox(toTitleCase(fk), fk)
                      )}

                      <Divider sx={{ borderColor: BORDER, my: 2 }} />

                      {/* Quantitative Avg VPT */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "minmax(150px, 185px) 1fr 1fr",
                          },
                          gap: { xs: 1.5, sm: 2 },
                          alignItems: "center",
                        }}
                      >
                        <Typography sx={labelSx}>
                          Quantitative Avg. VPT
                        </Typography>
                        <TextField
                          size="small"
                          value={formData?.quantitative_avg_vpt?.left ?? ""}
                          onChange={handleLeftRightText("left")}
                          placeholder="Left value"
                          disabled={isFetching}
                          autoComplete="off"
                          inputProps={{ inputMode: "decimal" }}
                          sx={inputSx}
                          fullWidth
                        />
                        <TextField
                          size="small"
                          value={formData?.quantitative_avg_vpt?.right ?? ""}
                          onChange={handleLeftRightText("right")}
                          placeholder="Right value"
                          disabled={isFetching}
                          autoComplete="off"
                          inputProps={{ inputMode: "decimal" }}
                          sx={inputSx}
                          fullWidth
                        />
                      </Box>
                    </Box>
                  </CaseSheetSection>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flexShrink: 0,
          justifyContent: "center",
          px: 2,
          py: 1.5,
          minHeight: 56,
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            width: "100%",
            minHeight: 40,
          }}
        >
          {!isFetching && !fetchError ? renderSaveButton("footer") : null}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default CaseSheetModal;